import { cva, type VariantProps } from 'class-variance-authority'
import { Eye, EyeOff, type LucideIcon, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * Input — single-line text input.
 * Pencil sources: Wkfaz jpZPt 28Yfp 3nrYb mHZeh cI2Xp (Default/Focused/Error/Disabled/WithIcon/Password)
 */
export const inputVariants = cva(
  [
    'flex w-full rounded-pill bg-ia-input-bg text-ia-text placeholder:text-ia-text-tertiary',
    'border border-ia-border transition-[border-color] duration-150',
    // Focus: border color change only — no ring/glow. Clean and calm.
    'focus-visible:outline-none focus-visible:border-ring',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-ia-elevated',
    'read-only:bg-ia-elevated read-only:cursor-default',
    'file:border-0 file:bg-transparent file:text-body-sm file:font-medium',
    'aria-[invalid=true]:border-ia-border-error aria-[invalid=true]:focus-visible:border-ia-border-error',
    '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-body-sm',
        md: 'h-10 px-3 text-body-md',
        lg: 'h-12 px-4 text-body-md',
      },
    },
    defaultVariants: { size: 'md' },
  },
)

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  leftIcon?: LucideIcon
  rightIcon?: LucideIcon
  /** visual error state — sets `aria-invalid` */
  invalid?: boolean
  /** show clear (×) button when value is present */
  clearable?: boolean
  onClear?: () => void
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      size,
      leftIcon,
      rightIcon,
      invalid,
      clearable,
      onClear,
      type = 'text',
      value,
      ...props
    },
    ref,
  ) => {
    const hasLeft = !!leftIcon
    const hasRight = !!rightIcon || (clearable && value)
    return (
      <div className="relative w-full">
        {hasLeft && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-ia-text-tertiary">
            <Icon icon={leftIcon!} size="sm" />
          </div>
        )}
        <input
          ref={ref}
          type={type}
          value={value}
          aria-invalid={invalid || undefined}
          className={cn(
            inputVariants({ size, className }),
            // Use numeric padding utilities (not arbitrary values) so they
            // beat the size variant's `px-3`. In Tailwind 4 arbitrary `pl-*`
            // loses to `px-*` because `padding-inline` emits after it.
            hasLeft && 'pl-9',
            hasRight && 'pr-9',
          )}
          {...props}
        />
        {rightIcon && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ia-text-tertiary">
            <Icon icon={rightIcon} size="sm" />
          </div>
        )}
        {clearable && value && !rightIcon && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-ia-text-tertiary hover:text-ia-text"
          >
            <Icon icon={X} size="sm" />
          </button>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'

/**
 * PasswordInput — Input variant with show/hide toggle.
 */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'type' | 'rightIcon'>
>(({ ...props }, ref) => {
  const [show, setShow] = React.useState(false)
  return (
    <div className="relative w-full">
      <Input ref={ref} type={show ? 'text' : 'password'} {...props} />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-ia-text-tertiary hover:text-ia-text"
      >
        <Icon icon={show ? EyeOff : Eye} size="sm" />
      </button>
    </div>
  )
})
PasswordInput.displayName = 'PasswordInput'
